import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ArrowUpRight } from "lucide-react";
import { useStore } from "@/context/Store";
import { img, inr, off } from "@/lib/api";
import { QuickView } from "@/components/QuickView";
import { CardControls } from "@/components/CardControls";
import { ReelVideo } from "@/components/ReelVideo";
import { Stars } from "@/components/Reviews";

export const lookFrames = (p) => (p.convertible && p.looks?.length ? p.looks.map((l) => ({ ...l, src: l.image || p.images?.[0], real: !!l.image })) : [{ number: 1, name: "", src: p.images?.[0], real: true }]);

export const WishButton = ({ p, className = "" }) => {
  const { wish, toggleWish } = useStore();
  const [pop, setPop] = useState(0);
  const liked = wish.includes(p.id);
  return (
    <button aria-label={liked ? "Remove from wishlist" : "Add to wishlist"} aria-pressed={liked} onClick={(e) => { e.preventDefault(); toggleWish(p.id); setPop((n) => n + 1); }}
      className={`glass rounded-full w-9 h-9 flex items-center justify-center transition-transform hover:scale-110 ${className}`} data-testid={`wish-${p.slug}`}>
      <Heart key={pop} className={`w-4 h-4 ${pop ? "heartpop" : ""} ${liked ? "fill-[var(--rose)] text-[var(--rose)]" : "text-[var(--ink)]"}`} strokeWidth={1.6} />
    </button>
  );
};

const StyleDots = ({ frames, i, setI, slug }) => (
  <div className="flex gap-1.5" role="group" aria-label="Ways to wear">
    {frames.map((f, k) => (
      <button key={k} onClick={(e) => { e.preventDefault(); setI(k); }} aria-label={`Style ${f.number}: ${f.name}`} aria-pressed={k === i}
        className={`w-7 h-7 rounded-full text-[11px] font-semibold flex items-center justify-center transition-colors duration-300 ${k === i ? "bg-[var(--ox)] text-white" : "glass text-[var(--ink)] hover:bg-white"}`} data-testid={`style-${slug}-${k + 1}`}>{f.number}</button>
    ))}
  </div>
);

const CardImage = ({ p, frames, i, setI }) => {
  const [hover, setHover] = useState(false), [touch, setTouch] = useState(null);
  const altIdx = frames.findIndex((f, k) => k > 0 && f.real && f.src !== frames[0].src);
  const shown = hover && i === 0 && altIdx > 0 ? altIdx : i;
  const swipe = (e) => { if (touch === null) return; const dx = e.changedTouches[0].clientX - touch; if (Math.abs(dx) > 40) setI((x) => (x + (dx < 0 ? 1 : -1) + frames.length) % frames.length); setTouch(null); };
  return (
    <Link to={`/product/${p.slug}`} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} onTouchStart={(e) => setTouch(e.touches[0].clientX)} onTouchEnd={swipe}
      className="block relative aspect-[3/4] overflow-hidden bg-[var(--stone)]" aria-label={p.name}>
      {frames.map((f, k) => (
        <img key={k} src={img(f.src)} alt={f.name ? `${p.name} worn as ${f.name}` : p.name} loading="lazy" decoding="async"
          className={`absolute inset-0 w-full h-full object-cover transition-[opacity,transform] duration-700 ease-out ${k === shown ? "opacity-100" : "opacity-0"} group-hover:scale-[1.05]`} />
      ))}
      {p.videos?.[0] && <ReelVideo src={p.videos[0]} poster={frames[0]?.src} active={shown === 0} testid={`reel-${p.slug}`} />}
      <div className="absolute inset-0 bg-gradient-to-t from-[rgba(74,39,50,.35)] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      {frames.length > 1 && frames[shown].name && <span className="hidden sm:block absolute bottom-3 right-3 max-w-[58%] truncate glass text-[10px] eyebrow px-2.5 py-1.5 transition-opacity" data-testid={`look-caption-${p.slug}`}>Style {frames[shown].number} · {frames[shown].name}</span>}
    </Link>
  );
};

export const ProductCard = ({ p, tall }) => {
  const frames = lookFrames(p);
  const [i, setI] = useState(0), [quick, setQuick] = useState(false), [info, setInfo] = useState(false);
  const ways = p.convertible ? p.looks.length : 0;
  return (
    <article className={`group relative transition-transform duration-500 hover:-translate-y-1 ${tall ? "lg:mt-14" : ""}`} data-testid={`product-card-${p.slug}`}>
      <div className="relative shadow-[0_0_0_0_rgba(0,0,0,0)] group-hover:shadow-[0_30px_60px_-30px_rgba(74,39,50,.45)] transition-shadow duration-500">
        <CardImage p={p} frames={frames} i={i} setI={setI} />
        <div className="absolute top-3 left-3 right-14 flex flex-col gap-1.5 items-start pointer-events-none">
          {ways > 1 && <span className="bg-[var(--ox)] text-white eyebrow text-[0.5rem] sm:text-[0.55rem] px-2 sm:px-2.5 py-1.5" data-testid={`ways-badge-${p.slug}`}>{ways} ways<span className="hidden sm:inline"> to wear</span></span>}
          {off(p) > 0 && <span className="glass eyebrow text-[0.55rem] px-2 py-1 text-[var(--rose-deep)]">−{off(p)}%</span>}
          {p.total_stock === 0 && <span className="bg-[var(--ink)] text-white eyebrow text-[0.55rem] px-2 py-1">Sold out</span>}
        </div>
        <WishButton p={p} className="absolute top-3 right-3" />
        {ways > 1 && <div className="absolute left-3 bottom-3"><StyleDots frames={frames} i={i} setI={setI} slug={p.slug} /></div>}
        <span className="absolute left-0 bottom-0 h-[2px] w-full bg-[var(--rose)] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
      </div>
      <div className="pt-3.5 text-sm">
        <div className="flex justify-between gap-3">
          <div className="min-w-0"><p className="text-[var(--taupe)] text-[0.62rem] eyebrow">{p.category}</p><Link to={`/product/${p.slug}`} className="font-semibold hover:text-[var(--rose-deep)] block truncate mt-0.5" data-testid={`card-name-${p.slug}`}>{p.name}</Link></div>
          <p className="text-right whitespace-nowrap font-medium" data-testid={`card-price-${p.slug}`}>{inr(p.price)}{p.compare_price > p.price && <span className="block text-xs line-through text-[var(--taupe)] font-normal">{inr(p.compare_price)}</span>}</p>
        </div>
        {p.rating_count > 0 && <p className="mt-1 flex items-center gap-1.5 text-xs text-[var(--taupe)]" data-testid={`card-rating-${p.slug}`}><Stars n={Math.round(p.rating_avg)} size="w-3 h-3" /> {p.rating_avg} ({p.rating_count})</p>}
        {ways > 1 && <Link to={`/product/${p.slug}#looks`} className="arrow-link mt-1.5 inline-flex items-center gap-1 text-xs text-[var(--rose-deep)] font-medium" data-testid={`view-looks-${p.slug}`}>View all looks <ArrowUpRight className="w-3.5 h-3.5" /></Link>}
        <CardControls p={p} info={info} setInfo={setInfo} openQuick={() => setQuick(true)} />
      </div>
      <QuickView p={p} open={quick} setOpen={setQuick} />
    </article>
  );
};
